
import { supabaseDirectClient } from '@/lib/supabase/directClient';
import { AIPrediction, PredictionData } from './types';
import { startOfWeek } from 'date-fns';

export const checkTableExists = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabaseDirectClient
      .from('ai_predictions')
      .select('count')
      .limit(0);
    
    if (error) {
      console.error('Error checking if predictions table exists:', error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Exception checking if predictions table exists:', err);
    return false;
  }
};

export const checkForCurrentWeekPredictions = async (): Promise<AIPrediction[] | null> => {
  try {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    console.log('[Database] Checking for predictions after:', weekStart.toISOString());

    // Retrieve all columns including chart data
    const { data, error } = await supabaseDirectClient
      .from('ai_predictions')
      .select('*')
      .gte('prediction_date', weekStart.toISOString())
      .order('prediction_date', { ascending: false });

    if (error) {
      console.error('[Database] Error checking current week predictions:', error);
      return null;
    }

    if (data && data.length > 0) {
      // Get only the latest prediction for each metal
      const latestPredictionsMap: Record<string, AIPrediction> = {};
      data.forEach((item: AIPrediction) => {
        if (!latestPredictionsMap[item.metal]) {
          latestPredictionsMap[item.metal] = item;
        }
      });
      const latestPredictions = Object.values(latestPredictionsMap);
      console.log('[Database] Found', latestPredictions.length, 'latest predictions with chart data');
      return latestPredictions;
    }

    console.log('[Database] No predictions found for current week');
    return null;
  } catch (err) {
    console.error('[Database] Exception checking current week predictions:', err);
    return null;
  }
};

export const savePredictionsToDatabase = async (predictions: PredictionData[]): Promise<boolean> => {
  try {
    const tableExists = await checkTableExists();
    
    if (!tableExists) {
      console.error('[Database] Cannot save predictions: ai_predictions table does not exist');
      return false;
    }
    
    const now = new Date();
    
    // Map the data ensuring all fields are included
    const dataToInsert = predictions.map(pred => {
      const predictionDate = pred.prediction_date || now.toISOString();
      console.log(`[Database] Formatting prediction with chart data for metal: ${pred.metal}`);
      
      return {
        metal: pred.metal,
        prediction: pred.prediction,
        price_trend: pred.price_trend,
        confidence_score: pred.confidence_score,
        prediction_date: predictionDate,
        // Chart data fields
        time_series: pred.time_series || null,
        scenarios: pred.scenarios || null,
        current_price: pred.current_price || null,
        target_price: pred.target_price || null,
        target_percentage: pred.target_percentage || null,
        timeframe: pred.timeframe || null
      };
    });
    
    console.log('[Database] Saving predictions with chart data to database:', dataToInsert.length, 'records');
    
    const { data, error } = await supabaseDirectClient
      .from('ai_predictions')
      .insert(dataToInsert)
      .select();
    
    if (error) {
      console.error('[Database] Error saving predictions to database:', error);
      return false;
    }
    
    console.log('[Database] Predictions with chart data saved successfully:', data?.length || 0, 'records');
    return true;
  } catch (err) {
    console.error('[Database] Exception saving predictions to database:', err);
    return false;
  }
};
